import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { REPAIRS_STORE } from '../../repairs';
import { MAINTENANCE_STORE } from '../../maintenance';
import { Repair, RepairStatus, RepairPriority } from '../../../../shared/definitions/repair';
import { Maintenance, MaintenanceStatus } from '../../../../shared/definitions/maintenance';
import { TaskStatus } from '../../../../shared/definitions/task-card.model';
import type { UnifiedTask, TaskCard } from '../models/task-management.model';
import type { ServiceProvider } from '../../../../shared/definitions/service-provider';
import type { ITaskManagementStore } from './task-management-store.interface';
import { interventionPhaseFromRepair } from '../../repairs/repair-detail/services/intervention-phase.util';
import { activeVisit, toVisitDate } from '../../repairs/repair-detail/services/repair-visit.util';

/**
 * Task Management aggregation store — unified view over repairs + maintenance.
 *
 * Replaces the legacy `TaskManagementService` retired as part of Phase C
 * (2026-04-20). Reads repairs from `REPAIRS_STORE` and maintenance from
 * `MAINTENANCE_STORE`.
 */
@Injectable({ providedIn: 'root' })
export class TaskManagementFirestoreStore implements ITaskManagementStore {
  private readonly repairsStore = inject(REPAIRS_STORE);
  private readonly maintenanceStore = inject(MAINTENANCE_STORE);
  private readonly destroyRef = inject(DestroyRef);

  private readonly repairsList$ = toObservable(computed(() => [...this.repairsStore.repairs()]));
  private readonly maintenanceList$ = toObservable(computed(() => [...this.maintenanceStore.maintenance()]));

  private readonly _tasks = signal<readonly UnifiedTask[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly tasks = this._tasks.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._tasks().length);

  readonly byStatus: Signal<Readonly<Record<TaskStatus, readonly UnifiedTask[]>>> = computed(() => {
    const buckets: Record<string, UnifiedTask[]> = {};
    for (const t of this._tasks()) {
      (buckets[t.status] ??= []).push(t);
    }
    return buckets as unknown as Readonly<Record<TaskStatus, readonly UnifiedTask[]>>;
  });

  constructor() {
    combineLatest([
      this.repairsList$,
      this.maintenanceList$
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([repairs, maintenance]) =>
          this._tasks.set(this.convertToUnifiedTasks(repairs, maintenance)),
        error: err =>
          this._lastError.set(err instanceof Error ? err : new Error(String(err)))
      });
  }

  async refresh(): Promise<void> {
    // Underlying services' streams auto-sync; nothing to re-fetch here.
    return;
  }

  getTasksForProperty(propertyUID: string): readonly UnifiedTask[] {
    return this._tasks().filter(t => t.propertyUID === propertyUID);
  }

  getTasksByStatus(status: TaskStatus): readonly UnifiedTask[] {
    return this._tasks().filter(t => t.status === status);
  }

  getTaskById(taskId: string): UnifiedTask | null {
    return this._tasks().find(t => t.id === taskId) ?? null;
  }

  toKanbanCards(tasks?: readonly UnifiedTask[]): TaskCard[] {
    const source = tasks ?? this._tasks();
    return source.map(task => ({
      id: task.id,
      title: task.title,
      description: task.description,
      type: task.type,
      status: task.status,
      priority: task.priority,
      propertyUID: task.propertyUID,
      propertyName: task.propertyName,
      propertyAddress: '',
      assignedProvider: task.assignedProvider,
      assignedTo: task.assignedTo,
      assignedProviderName: task.assignedProviderName,
      assignedDate: task.assignedDate,
      scheduledDate: task.scheduledDate,
      dueDate: task.dueDate,
      estimatedDuration: task.estimatedDuration,
      progress: task.progress,
      completedDate: task.completedDate,
      attachments: task.attachments,
      cost: task.cost,
      estimatedCost: task.estimatedCost,
      actualCost: task.actualCost,
      urgencyReason: task.urgencyReason,
      specialties: task.specialties,
      createdAt: task.createdAt,
      updatedAt: task.updatedAt,
      phase:
        task.type === 'repair'
          ? interventionPhaseFromRepair(task.originalData as Repair)
          : undefined
    }));
  }

  async updateTaskStatus(taskId: string, newStatus: TaskStatus): Promise<void> {
    try {
      const task = this._tasks().find(t => t.id === taskId);
      if (!task) throw new Error(`Task with ID ${taskId} not found`);

      if (task.type === 'repair') {
        const repair = task.originalData as Repair;
        repair.status = this.mapTaskStatusToRepairStatus(newStatus);
        repair.updatedAt = new Date();
        if (newStatus === TaskStatus.COMPLETED) repair.completedDate = new Date();
        await this.repairsStore.updateRepair(repair);
      } else {
        const maintenance = task.originalData as Maintenance;
        maintenance.status = this.mapTaskStatusToMaintenanceStatus(newStatus);
        maintenance.updatedAt = new Date();
        if (newStatus === TaskStatus.COMPLETED) maintenance.completedDate = new Date();
        await this.maintenanceStore.updateMaintenance(maintenance);
      }
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async assignServiceProvider(taskId: string, provider: ServiceProvider): Promise<void> {
    try {
      const task = this._tasks().find(t => t.id === taskId);
      if (!task) throw new Error(`Task with ID ${taskId} not found`);

      if (task.type === 'repair') {
        const repair = task.originalData as Repair;
        repair.assignedTo = provider.providerUID;
        repair.updatedAt = new Date();
        await this.repairsStore.updateRepair(repair);
      } else {
        const maintenance = task.originalData as Maintenance;
        maintenance.assignedTo = provider.providerUID;
        maintenance.updatedAt = new Date();
        await this.maintenanceStore.updateMaintenance(maintenance);
      }
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async updateTaskSchedule(taskId: string, scheduledDate: Date): Promise<void> {
    try {
      const task = this._tasks().find(t => t.id === taskId);
      if (!task) throw new Error(`Task with ID ${taskId} not found`);

      if (task.type === 'repair') {
        const repair = task.originalData as Repair;
        repair.scheduledDate = scheduledDate;
        repair.updatedAt = new Date();
        await this.repairsStore.updateRepair(repair);
      } else {
        const maintenance = task.originalData as Maintenance;
        maintenance.scheduledDate = scheduledDate;
        maintenance.updatedAt = new Date();
        await this.maintenanceStore.updateMaintenance(maintenance);
      }
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async updateTaskCompletionData(
    taskId: string,
    completionData: {
      completionNotes?: string;
      rating?: number;
      costActual?: number;
      durationActual?: number;
      issues?: string[];
      recommendations?: string;
    }
  ): Promise<void> {
    // Try repairs first, fall back to maintenance. Matches the legacy two-try
    // lookup — a task only exists in one of the two collections.
    const repair = this.repairsStore.getRepairById(taskId);
    if (repair) {
      try {
        await this.repairsStore.updateRepair({
          ...repair,
          ...completionData,
          updatedAt: new Date()
        });
        return;
      } catch (err) {
        this._lastError.set(err instanceof Error ? err : new Error(String(err)));
        throw err;
      }
    }

    try {
      const maintenance = this.maintenanceStore.getMaintenanceById(taskId);
      if (maintenance) {
        await this.maintenanceStore.updateMaintenance({
          ...maintenance,
          ...completionData,
          updatedAt: new Date()
        });
        return;
      }
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw new Error(`Task ${taskId} not found`, { cause: err });
    }
  }

  // --- internals: conversion + mapping ---

  private convertToUnifiedTasks(repairs: Repair[], maintenance: Maintenance[]): UnifiedTask[] {
    return [
      ...repairs.map(r => this.repairToUnifiedTask(r)),
      ...maintenance.map(m => this.maintenanceToUnifiedTask(m))
    ];
  }

  private repairToUnifiedTask(repair: Repair): UnifiedTask {
    // The modern flow schedules a *visit* (with its own date + provider name)
    // rather than stamping the legacy `scheduledDate` / `assignedTo` fields, so
    // fall back to the active visit when those are absent. Without this the
    // "Date prévue" and "Prestataire" columns stay empty for visit-scheduled
    // repairs. `toVisitDate` also coerces a raw Firestore Timestamp to a Date.
    const visit = activeVisit(repair.visits);
    const scheduled =
      toVisitDate(repair.scheduledDate) ??
      (visit ? toVisitDate(visit.scheduledAt) : null) ??
      undefined;
    return {
      id: repair.repairUID || '',
      type: 'repair',
      title: repair.title,
      description: repair.description,
      status: this.mapRepairStatusToTaskStatus(repair.status),
      priority: this.mapRepairPriorityToTaskPriority(repair.priority),
      propertyUID: repair.propertyUID,
      propertyName: repair.propertyName || '',
      assignedTo: repair.assignedTo,
      assignedProviderName: visit?.providerName,
      scheduledDate: scheduled,
      dueDate: scheduled,
      completedDate: repair.completedDate,
      attachments: repair.attachments?.length || 0,
      cost: repair.actualCost || repair.estimatedCost,
      estimatedCost: repair.estimatedCost,
      actualCost: repair.actualCost,
      specialties: this.getRepairSpecialties(repair),
      notes: repair.notes,
      originalData: repair,
      createdAt: repair.createdAt,
      updatedAt: repair.updatedAt
    };
  }

  private maintenanceToUnifiedTask(maintenance: Maintenance): UnifiedTask {
    return {
      id: maintenance.maintenanceUID || '',
      type: 'maintenance',
      title: maintenance.title,
      description: maintenance.description,
      status: this.mapMaintenanceStatusToTaskStatus(maintenance.status),
      priority: this.mapMaintenancePriorityToTaskPriority(maintenance.priority),
      propertyUID: maintenance.propertyUID ?? '',
      propertyName: maintenance.propertyName || '',
      assignedTo: maintenance.assignedTo,
      scheduledDate: maintenance.scheduledDate,
      dueDate: maintenance.scheduledDate,
      estimatedDuration: maintenance.estimatedDuration,
      completedDate: maintenance.completedDate,
      attachments: (maintenance.attachments?.length || 0) + (maintenance.documentUIDs?.length || 0),
      cost: maintenance.actualCost || maintenance.estimatedCost,
      estimatedCost: maintenance.estimatedCost,
      actualCost: maintenance.actualCost,
      specialties: this.getMaintenanceSpecialties(maintenance),
      notes: maintenance.notes,
      originalData: maintenance,
      createdAt: maintenance.createdAt,
      updatedAt: maintenance.updatedAt
    };
  }

  private mapRepairStatusToTaskStatus(status: RepairStatus): TaskStatus {
    switch (status) {
      case RepairStatus.Pending: return TaskStatus.PLANNED;
      case RepairStatus.Assigned: return TaskStatus.SCHEDULED;
      case RepairStatus.InProgress:
      case RepairStatus.WaitingForParts:
        return TaskStatus.IN_PROGRESS;
      case RepairStatus.Completed:
      case RepairStatus.Resolved:
        return TaskStatus.COMPLETED;
      case RepairStatus.Canceled: return TaskStatus.CANCELLED;
      default: return TaskStatus.PLANNED;
    }
  }

  private mapMaintenanceStatusToTaskStatus(status: MaintenanceStatus): TaskStatus {
    switch (status) {
      case MaintenanceStatus.Pending: return TaskStatus.PLANNED;
      case MaintenanceStatus.Scheduled: return TaskStatus.SCHEDULED;
      case MaintenanceStatus.InProgress: return TaskStatus.IN_PROGRESS;
      case MaintenanceStatus.Completed: return TaskStatus.COMPLETED;
      case MaintenanceStatus.Canceled: return TaskStatus.CANCELLED;
      case MaintenanceStatus.Overdue: return TaskStatus.IN_PROGRESS;
      default: return TaskStatus.PLANNED;
    }
  }

  private mapTaskStatusToRepairStatus(status: TaskStatus): RepairStatus {
    switch (status) {
      case TaskStatus.PLANNED: return RepairStatus.Pending;
      case TaskStatus.SCHEDULED: return RepairStatus.Assigned;
      case TaskStatus.IN_PROGRESS: return RepairStatus.InProgress;
      case TaskStatus.COMPLETED: return RepairStatus.Completed;
      case TaskStatus.CANCELLED: return RepairStatus.Canceled;
      default: return RepairStatus.Pending;
    }
  }

  private mapTaskStatusToMaintenanceStatus(status: TaskStatus): MaintenanceStatus {
    switch (status) {
      case TaskStatus.PLANNED: return MaintenanceStatus.Pending;
      case TaskStatus.SCHEDULED: return MaintenanceStatus.Scheduled;
      case TaskStatus.IN_PROGRESS: return MaintenanceStatus.InProgress;
      case TaskStatus.COMPLETED: return MaintenanceStatus.Completed;
      case TaskStatus.CANCELLED: return MaintenanceStatus.Canceled;
      default: return MaintenanceStatus.Pending;
    }
  }

  private mapRepairPriorityToTaskPriority(priority: RepairPriority): 'low' | 'medium' | 'high' | 'emergency' {
    switch (priority) {
      case RepairPriority.Low: return 'low';
      case RepairPriority.Medium: return 'medium';
      case RepairPriority.High: return 'high';
      case RepairPriority.Emergency: return 'emergency';
      default: return 'medium';
    }
  }

  private mapMaintenancePriorityToTaskPriority(priority?: string): 'low' | 'medium' | 'high' | 'emergency' {
    if (!priority) return 'medium';
    switch (priority.toLowerCase()) {
      case 'low': return 'low';
      case 'medium': return 'medium';
      case 'high': return 'high';
      case 'emergency':
      case 'urgent': return 'emergency';
      default: return 'medium';
    }
  }

  private getRepairSpecialties(repair: Repair): string[] {
    const specialtyMap: Record<string, string[]> = {
      electrical: ['Electrical Work'],
      plumbing: ['Plumbing'],
      structural: ['General Contracting', 'Construction'],
      appliance: ['Appliance Repair'],
      heating: ['HVAC', 'Heating'],
      cooling: ['HVAC', 'Air Conditioning'],
      roofing: ['Roofing'],
      flooring: ['Flooring', 'Carpentry'],
      other: ['General Maintenance']
    };
    return specialtyMap[repair.type] || ['General Maintenance'];
  }

  private getMaintenanceSpecialties(maintenance: Maintenance): string[] {
    const specialtyMap: Record<string, string[]> = {
      routine: ['General Maintenance'],
      inspection: ['Inspection', 'Property Assessment'],
      upgrade: ['Renovation', 'Improvement'],
      preventive: ['Preventive Maintenance'],
      seasonal: ['Seasonal Maintenance']
    };
    return specialtyMap[maintenance.type] || ['General Maintenance'];
  }
}
